
import React from "react";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";

export interface WizardStep {
  id: string;
  title: string;
  description?: string;
}

export interface WizardProps {
  steps: WizardStep[];
  currentStep: number;
  onChange?: (step: number) => void;
  wizardId?: string;
  className?: string;
}

export const Wizard: React.FC<WizardProps> = ({
  steps,
  currentStep,
  onChange,
  wizardId,
  className,
}) => {
  // Ensure currentStep is within bounds
  const safeCurrentStep = Math.max(0, Math.min(currentStep, steps.length - 1));
  
  const handleStepClick = (index: number) => {
    if (onChange && index <= safeCurrentStep) {
      onChange(index);
    }
  };

  return (
    <div className={cn("relative mb-8", className)} data-wizard-id={wizardId}>
      <div className="relative z-10 flex justify-between">
        {steps.map((step, index) => {
          const isActive = index === safeCurrentStep;
          const isCompleted = index < safeCurrentStep;
          const isClickable = index <= safeCurrentStep;
          
          return (
            <div
              key={step.id}
              className={cn(
                "flex flex-col items-center space-y-2",
                isClickable ? "cursor-pointer" : "cursor-not-allowed opacity-50"
              )}
              onClick={() => handleStepClick(index)}
            >
              <div
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-full border-2 text-xs font-semibold",
                  isActive
                    ? "border-primary bg-primary text-primary-foreground"
                    : isCompleted
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-gray-300 bg-white"
                )}
              >
                {isCompleted ? <Check className="h-4 w-4" /> : index + 1}
              </div>
              <div className="text-center">
                <p className={cn(
                  "text-sm font-medium",
                  isActive ? "text-gray-900" : "text-gray-500"
                )}>
                  {step.title}
                </p>
                {step.description && (
                  <p className="hidden text-xs text-gray-500 sm:block">
                    {step.description}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* The key fix is here - removing any animation class that might cause disappearance */}
      <div className="absolute top-4 left-0 right-0 -z-10 h-0.5 bg-gray-200">
        <div
          className="h-full bg-primary"
          style={{
            width: `${(safeCurrentStep / (steps.length - 1)) * 100}%`,
          }}
        />
      </div>
    </div>
  );
};
